//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { crl8, isAuthedSession } from '@app/curalate.globals';
import { SessionMode } from '@moderation-mode/constants/SessionMode';
import { resolveSessionMode } from '@moderation-mode/moderationModeResolver';

type SessionModeListener = () => void;

/**
 * The single authoritative live mode for every runtime consumer (render UI AND
 * the route guard). localStorage is persistence only — never a second live
 * source — so a swallowed write cannot desync the guard from the UI. Seeded
 * synchronously from resolveSessionMode and re-seeded when the session settles.
 */
export const createSessionModeStore = () => {
  const listeners = new Set<SessionModeListener>();
  let mode: SessionMode = resolveSessionMode();
  // The module-load seed above may have run before the session settled.
  let hasHydratedAuthedSession = isAuthedSession(crl8.session);

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const getMode = (): SessionMode => mode;

  const setMode = (nextMode: SessionMode) => {
    if (nextMode === mode) {
      return;
    }

    mode = nextMode;
    notify();
  };

  const hydrate = () => {
    hasHydratedAuthedSession = isAuthedSession(crl8.session);
    setMode(resolveSessionMode());
  };

  // Lazy seed so the guard reads an authoritative mode on the first hard-load
  // transition, before the explicit hydrate() on session resolve.
  const ensureHydrated = () => {
    if (!hasHydratedAuthedSession && isAuthedSession(crl8.session)) {
      hydrate();
    }
  };

  const subscribe = (listener: SessionModeListener) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
  };

  return { ensureHydrated, getMode, hydrate, setMode, subscribe };
};

export const sessionModeStore = createSessionModeStore();
